/**
 * Newsletter Success Handler
 * Manages AJAX form submissions and success/error messages for all newsletter forms
 */

// Prevent duplicate declaration if script is loaded multiple times
if (typeof NewsletterSuccessHandler === 'undefined') {

class NewsletterSuccessHandler {
    constructor() {
        this.overlay = null;
        this.modal = null;
        this.closeBtn = null;
        this.autoDismissTimer = null;
        // Test mode disabled - forms now submit naturally to Shopify
        this.testMode = false;

        // Log test mode status
        console.log('Newsletter Handler initialized. Test mode:', this.testMode, 'Hostname:', window.location.hostname);
        if (!this.testMode) {
            console.log('To enable test mode, add #test-newsletter to the URL');
        }

        this.init();
    }

    init() {
        // Create success modal HTML
        this.createSuccessModal();

        // Initialize all newsletter forms
        this.initializeForms();

        // Setup event listeners
        this.setupEventListeners();
    }

    createSuccessModal() {
        // Check if modal already exists
        if (document.getElementById('newsletter-success-overlay')) {
            this.overlay = document.getElementById('newsletter-success-overlay');
            this.modal = document.getElementById('newsletter-success-modal');
            this.closeBtn = this.modal.querySelector('.close-btn');
            return;
        }

        // Create overlay
        this.overlay = document.createElement('div');
        this.overlay.id = 'newsletter-success-overlay';
        this.overlay.className = 'newsletter-success-overlay';

        // Create modal
        this.modal = document.createElement('div');
        this.modal.id = 'newsletter-success-modal';
        this.modal.className = 'newsletter-success-modal';
        this.modal.setAttribute('role', 'dialog');
        this.modal.setAttribute('aria-labelledby', 'newsletter-success-title');
        this.modal.setAttribute('aria-describedby', 'newsletter-success-message');

        // Modal HTML content
        this.modal.innerHTML = `
            <button class="close-btn" aria-label="Close success message">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                    <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
            <div class="newsletter-success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
            <div class="newsletter-success-content">
                <h3 id="newsletter-success-title">Success!</h3>
                <p id="newsletter-success-message">Thank you for subscribing to our newsletter!</p>
            </div>
        `;

        // Append to body
        document.body.appendChild(this.overlay);
        document.body.appendChild(this.modal);

        // Get close button reference
        this.closeBtn = this.modal.querySelector('.close-btn');
    }

    initializeForms() {
        // Find all newsletter forms
        const forms = document.querySelectorAll(
            'form[action*="/contact"], form.newsletter-form, #newsletter-form, #contact_form, #ContactFooter, #ContactPopup'
        );

        forms.forEach(form => {
            // Check if it's a newsletter form by looking for email input and correct form type
            const emailInput = form.querySelector('input[type="email"]');
            const formTypeInput = form.querySelector('input[name="form_type"][value="customer"]');

            if (emailInput && formTypeInput) {
                this.attachFormHandler(form);
            }
        });
    }

    attachFormHandler(form) {
        // Don't intercept form submission - let it submit naturally to Shopify
        // Only add validation if needed
        form.addEventListener('submit', (e) => {
            const emailInput = form.querySelector('input[type="email"]');
            const email = emailInput ? emailInput.value : '';

            // Validate email before allowing submission
            if (!this.validateEmail(email)) {
                e.preventDefault();
                this.showMessage('error', 'Error!', 'Please enter a valid email address.');
                return false;
            }

            // Let the form submit naturally - don't prevent default
            // Shopify will handle the submission and redirect
        });
    }

    async handleFormSubmit(form) {
        // Get form data
        const formData = new FormData(form);
        const email = formData.get('contact[email]');

        // Validate email
        if (!this.validateEmail(email)) {
            this.showMessage('error', 'Error!', 'Please enter a valid email address.');
            return;
        }

        // Add loading state
        form.classList.add('newsletter-form-submitting');
        const submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn) {
            submitBtn.disabled = true;
        }

        try {
            // Test mode - skip actual submission and show success
            if (this.testMode) {
                console.log('Test Mode: Simulating successful newsletter submission');
                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 500));

                // Show success message
                this.showMessage('success', 'Success!', 'Thank you for subscribing to our newsletter!');

                // Clear form
                form.reset();

                // Handle popup form
                if (form.id === 'ContactPopup' || form.closest('.halo-newsletter-popup')) {
                    this.setCookie('newsletter-popup', 'closed', 30);
                    const popup = document.querySelector('.halo-newsletter-popup');
                    if (popup) {
                        setTimeout(() => {
                            popup.style.display = 'none';
                            document.body.classList.remove('newsletter-show');
                        }, 2000);
                    }
                }

                return; // Exit early in test mode
            }

            // Submit form via AJAX (production mode)
            const response = await fetch(form.action || '/contact', {
                method: 'POST',
                body: formData,
                headers: {
                    'Accept': 'application/json, text/javascript',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            });

            // Check response
            if (response.ok) {
                // Success
                this.showMessage('success', 'Success!', 'Thank you for subscribing to our newsletter!');

                // Clear form
                form.reset();

                // Set cookie to prevent popup from showing again (if it's popup form)
                if (form.id === 'ContactPopup' || form.closest('.halo-newsletter-popup')) {
                    this.setCookie('newsletter-popup', 'closed', 30);

                    // Hide popup if visible
                    const popup = document.querySelector('.halo-newsletter-popup');
                    if (popup) {
                        setTimeout(() => {
                            popup.style.display = 'none';
                            document.body.classList.remove('newsletter-show');
                        }, 2000);
                    }
                }
            } else {
                // Error
                const errorText = await response.text();
                let errorMessage = 'An error occurred. Please try again.';

                // Try to parse error message
                if (errorText.includes('already subscribed')) {
                    errorMessage = 'This email is already subscribed to our newsletter.';
                } else if (errorText.includes('invalid')) {
                    errorMessage = 'Please enter a valid email address.';
                }

                this.showMessage('error', 'Oops!', errorMessage);
            }
        } catch (error) {
            // Network error
            console.error('Newsletter submission error:', error);
            this.showMessage('error', 'Error!', 'Unable to submit. Please check your connection and try again.');
        } finally {
            // Remove loading state
            form.classList.remove('newsletter-form-submitting');
            if (submitBtn) {
                submitBtn.disabled = false;
            }
        }
    }

    showMessage(type, title, message) {
        // Update modal content
        const titleElement = this.modal.querySelector('#newsletter-success-title');
        const messageElement = this.modal.querySelector('#newsletter-success-message');
        const iconElement = this.modal.querySelector('.newsletter-success-icon svg');

        titleElement.textContent = title;
        messageElement.textContent = message;

        // Update modal class and icon for error
        if (type === 'error') {
            this.modal.classList.add('error');
            iconElement.innerHTML = '<path d="M6 18L18 6M6 6l12 12" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>';
        } else {
            this.modal.classList.remove('error');
            iconElement.innerHTML = '<path d="M20 6L9 17l-5-5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>';
        }

        // Show modal
        this.showModal();

        // Auto dismiss after 3 seconds (only for success)
        if (type === 'success') {
            this.autoDismissTimer = setTimeout(() => {
                this.hideModal();
            }, 3000);
        }
    }

    showModal() {
        // Ensure elements exist
        if (!this.overlay || !this.modal) {
            console.error('Newsletter modal elements not found');
            return;
        }

        // Clear any existing timer
        if (this.autoDismissTimer) {
            clearTimeout(this.autoDismissTimer);
        }

        // Add active classes
        this.overlay.classList.add('active');
        this.modal.classList.add('active');
        document.body.classList.add('newsletter-success-active');

        // Focus on modal for accessibility
        this.modal.focus();
    }

    hideModal() {
        // Ensure elements exist
        if (!this.overlay || !this.modal) {
            return;
        }

        // Remove active classes
        this.overlay.classList.remove('active');
        this.modal.classList.remove('active');
        document.body.classList.remove('newsletter-success-active');

        // Clear timer
        if (this.autoDismissTimer) {
            clearTimeout(this.autoDismissTimer);
            this.autoDismissTimer = null;
        }
    }

    setupEventListeners() {
        // Only setup listeners if elements exist
        if (!this.overlay || !this.closeBtn || !this.modal) {
            console.warn('Newsletter success modal elements not found, skipping event listeners');
            return;
        }

        // Close on overlay click
        this.overlay.addEventListener('click', () => {
            this.hideModal();
        });

        // Close on close button click
        this.closeBtn.addEventListener('click', () => {
            this.hideModal();
        });

        // Close on escape key
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal && this.modal.classList.contains('active')) {
                this.hideModal();
            }
        });
    }

    validateEmail(email) {
        const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return re.test(email);
    }

    setCookie(name, value, days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        const expires = 'expires=' + date.toUTCString();
        document.cookie = name + '=' + value + ';' + expires + ';path=/';
    }
}

// Global instance variable
let newsletterHandlerInstance = null;

// Initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        if (!newsletterHandlerInstance) {
            newsletterHandlerInstance = new NewsletterSuccessHandler();
        }
    });
} else {
    if (!newsletterHandlerInstance) {
        newsletterHandlerInstance = new NewsletterSuccessHandler();
    }
}

// Re-initialize when new forms are added dynamically
document.addEventListener('shopify:section:load', () => {
    if (newsletterHandlerInstance) {
        // Re-initialize forms for newly loaded sections
        newsletterHandlerInstance.initializeForms();
    } else {
        newsletterHandlerInstance = new NewsletterSuccessHandler();
    }
});

} // End of if statement preventing duplicate declaration